// src/App.js
import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useTranslation } from 'react-i18next';

import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CategoryIcon from '@mui/icons-material/Category';
import ClassIcon from '@mui/icons-material/Class';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import Products from './components/AdminDashboard/Products';
import Categories from './components/AdminDashboard/Categories';
import Coupons from './components/AdminDashboard/Coupons';
import Advertisements from './components/AdminDashboard/Advertisements';
import Inventory from './components/AdminDashboard/Inventory';
import BrowseProducts from './components/UserDashboard/BrowseProducts';
import MyOrders from './components/UserDashboard/MyOrders';
import MyInvoices from './components/UserDashboard/MyInvoices';
import Cart from './components/UserDashboard/Cart'; // Import the Cart component
import CustomerDashboard from './components/UserDashboard/CustomerDashboard';
import AllUsers from './components/AdminDashboard/AllUsers';
import LoginPage from './components/LoginPage';
import AdminOrders from './components/AdminDashboard/AdminOrders';
import Invoices from './components/AdminDashboard/Invoices';
import MyCompany from './components/AdminDashboard/MyCompany';
import AdminDashboard from './components/AdminDashboard/AdminDahsboard';
import Reporting from './components/AdminDashboard/Reporting';
import UserProfile from './components/UserProfile/UserProfile';
import ProductDetail from './components/UserDashboard/ProductDetail';

import GADashboard from './components/GlobalAdmin/GADashboard';
import GACompanies from './components/GlobalAdmin/GACompanies';
import GACompanyDetail from './components/GlobalAdmin/GACompanyDetail';
import GAReporting from './components/GlobalAdmin/GAReporting';
import GAPlans from './components/GlobalAdmin/plans/GAPlans';
import GAPlanBuilder from './components/GlobalAdmin/plans/GAPlanBuilder';
import GASettings from './components/GlobalAdmin/GASettings';
import GASupportMessages from './components/GlobalAdmin/GASupportMessages';
import Subscription from './components/AdminDashboard/Subscription';
import FeatureGate from './components/shared/FeatureGate';
import SubscriptionGate, { lockNavForUnsubscribed } from './components/shared/SubscriptionGate';
import { EntitlementsProvider, useEntitlements } from './contexts/EntitlementsContext';
import { EntitlementSnackbar } from './components/ui';

import AppShell from './components/AppShell/AppShell';
import CompanySelectPage from './components/CompanySelectPage';
import DesignSystemPreview from './components/ui/DesignSystemPreview';
import { lightTheme, darkTheme } from './themes/smartinventoryTheme';
import { CartProvider } from './contexts/CartContext'; // Import CartProvider
import { useCart } from './contexts/CartContext';
import useCompanyName from './hooks/useCompanyName';

// The three roles differ only in this nav array — grouped per
// design_handoff_smartinventory_redesign/README.md Step 2. `mobilePriority`
// marks the up-to-4 items AppShell surfaces in the mobile bottom bar.
const buildAdminNav = (t) => [
  {
    label: t('Overview'),
    items: [
      { label: t('Dashboard'), to: '/dashboard', icon: DashboardIcon, mobilePriority: true },
      { label: t('Reports'), to: '/reporting', icon: AssessmentIcon, feature: 'reporting' },
    ],
  },
  {
    label: t('Catalog'),
    items: [
      { label: t('Products'), to: '/products', icon: CategoryIcon, mobilePriority: true },
      { label: t('Categories'), to: '/categories', icon: ClassIcon, feature: 'categories' },
      { label: t('Inventory'), to: '/inventory', icon: Inventory2Icon, feature: 'inventory' },
    ],
  },
  {
    label: t('Sales'),
    items: [
      { label: t('Client Orders'), to: '/client-orders', icon: ReceiptLongIcon, mobilePriority: true, feature: 'orders.monthly' },
      { label: t('Invoices'), to: '/invoices', icon: RequestQuoteIcon, mobilePriority: true, feature: 'invoices' },
      { label: t('Coupons'), to: '/coupons', icon: LocalOfferIcon, feature: 'coupons.active.max' },
      { label: t('Advertisements'), to: '/advertisements', icon: CampaignIcon, feature: 'advertisements' },
    ],
  },
  {
    label: t('Company'),
    items: [
      { label: t('Users'), to: '/users', icon: PeopleIcon },
      { label: t('My Company'), to: '/my-company', icon: BusinessIcon },
      { label: t('Subscription', { defaultValue: 'Subscription' }), to: '/subscription', icon: CreditCardIcon },
      { label: t('My Profile'), to: '/profile', icon: AccountCircleIcon },
    ],
  },
];

const buildUserNav = (t, cartCount) => [
  {
    label: t('Overview'),
    items: [
      { label: t('Dashboard'), to: '/dashboard', icon: DashboardIcon, mobilePriority: true },
    ],
  },
  {
    label: t('Shop'),
    items: [
      { label: t('Browse Products', { defaultValue: 'Browse Products' }), to: '/shop', icon: Inventory2Icon, mobilePriority: true },
      { label: t('Shopping Cart'), to: '/cart', icon: ShoppingCartIcon, badge: cartCount, mobilePriority: true },
    ],
  },
  {
    label: t('Orders'),
    items: [
      { label: t('My Orders'), to: '/my-orders', icon: ReceiptLongIcon, mobilePriority: true },
      { label: t('Invoices'), to: '/invoices', icon: RequestQuoteIcon },
    ],
  },
  {
    label: t('Account'),
    items: [
      { label: t('My Profile'), to: '/profile', icon: AccountCircleIcon },
    ],
  },
];

const buildGlobalAdminNav = (t) => [
  {
    label: t('Overview'),
    items: [
      { label: t('Dashboard'), to: '/ga/dashboard', icon: DashboardIcon, mobilePriority: true },
      { label: t('Reporting'), to: '/ga/reporting', icon: AssessmentIcon, mobilePriority: true },
    ],
  },
  {
    label: t('Platform'),
    items: [
      { label: t('Companies'), to: '/ga/companies', icon: BusinessIcon, mobilePriority: true },
      { label: t('Plans & Pricing', { defaultValue: 'Plans & Pricing' }), to: '/ga/plans', icon: SellOutlinedIcon },
      { label: t('ga.support.title', { defaultValue: 'Support messages' }), to: '/ga/support', icon: SupportAgentIcon },
      { label: t('Settings', { defaultValue: 'Settings' }), to: '/ga/settings', icon: SettingsOutlinedIcon },
    ],
  },
];

// The customer shell needs the cart item count for the Cart nav badge, and
// useCart() only works inside <CartProvider>'s subtree — so this small
// wrapper (not App() itself) is what actually builds the customer nav.
// Same idea for the admin shell: nav items with a `feature` key get a lock
// (not hidden — discoverability is the upsell) when the company's plan
// doesn't include that feature. With no subscription at all, everything
// but My Company / Subscription / My Profile is locked and the gate sends
// the admin to plan selection.
function AdminShell({ toggleTheme, isDarkMode, workspace }) {
  const { t } = useTranslation();
  const { can, subscribed, status } = useEntitlements();
  const loaded = status === 'ready' || status === 'refreshing';
  const featureLocked = buildAdminNav(t).map((group) => ({
    ...group,
    items: group.items.map((item) => (item.feature && !can(item.feature) ? { ...item, locked: true } : item)),
  }));
  const nav = loaded && !subscribed ? lockNavForUnsubscribed(featureLocked) : featureLocked;
  return (
    <AppShell nav={nav} workspace={workspace} toggleTheme={toggleTheme} isDarkMode={isDarkMode} enableHelp>
      <SubscriptionGate>
        <Outlet />
      </SubscriptionGate>
    </AppShell>
  );
}

function UserShell({ toggleTheme, isDarkMode, workspace }) {
  const { t } = useTranslation();
  const { cart } = useCart();
  const nav = buildUserNav(t, cart?.itemCount || 0);
  return (
    <AppShell nav={nav} workspace={workspace} toggleTheme={toggleTheme} isDarkMode={isDarkMode}>
      <Outlet />
    </AppShell>
  );
}

function App() {
  const { t } = useTranslation();
  const companyName = useCompanyName();

  const [auth, setAuth] = useState({
    username: localStorage.getItem('username'),
    userRole: localStorage.getItem('userRole') || 'user',
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('isDarkMode');
    if (stored !== null) return stored === 'true';
    // No explicit choice yet — respect the OS preference on first visit only;
    // once handleThemeToggle writes a value, that stored choice always wins.
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const newVal = !prev;
      localStorage.setItem('isDarkMode', newVal);
      return newVal;
    });
  };

  const isGlobalAdmin = auth.userRole === 'global_admin';
  const isAdminLike = auth.userRole === 'admin' || isGlobalAdmin;

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <CartProvider>
        <BrowserRouter>
          <EntitlementsProvider>
          <EntitlementSnackbar />
          <Routes>
            <Route path="/login" element={<LoginPage setAuth={setAuth} toggleTheme={handleThemeToggle} />} />
            <Route
              path="/select-company"
              element={auth.username ? <CompanySelectPage toggleTheme={handleThemeToggle} /> : <Navigate to="/login" replace />}
            />
            {/* Scratch route for the step-3 shared component library — not linked from any nav. */}
            <Route path="/dev/design-system" element={<DesignSystemPreview />} />

            {/* Global Admin routes */}
            <Route
              path="/ga"
              element={
                auth.username && isGlobalAdmin ? (
                  <AppShell
                    nav={buildGlobalAdminNav(t)}
                    workspace={{ company: t('Global Admin Console'), user: auth.username, role: 'global_admin' }}
                    toggleTheme={handleThemeToggle}
                    isDarkMode={isDarkMode}
                    enableHelp
                  >
                    <Outlet />
                  </AppShell>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            >
              <Route index element={<Navigate to="/ga/dashboard" replace />} />
              <Route path="dashboard" element={<GADashboard />} />
              <Route path="companies" element={<GACompanies />} />
              <Route path="companies/:companyId" element={<GACompanyDetail />} />
              <Route path="reporting" element={<GAReporting />} />
              <Route path="plans" element={<GAPlans />} />
              <Route path="plans/:planId" element={<GAPlanBuilder />} />
              <Route path="support" element={<GASupportMessages />} />
              <Route path="settings" element={<GASettings />} />
            </Route>

            {/* Authenticated area */}
            <Route path="/" element={auth.username ? <Outlet /> : <Navigate to="/login" replace />}>
              {isGlobalAdmin ? (
                // Redirect global admins hitting "/" to GA dashboard
                <Route index element={<Navigate to="/ga/dashboard" replace />} />
              ) : isAdminLike ? (
                <Route
                  element={
                    <AdminShell
                      workspace={{ company: companyName, companyId: localStorage.getItem('companyId'), user: auth.username, role: 'admin' }}
                      toggleTheme={handleThemeToggle}
                      isDarkMode={isDarkMode}
                    />
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="products" element={<Products />} />
                  <Route path="categories" element={<FeatureGate feature="categories" featureName={t('Categories')}><Categories /></FeatureGate>} />
                  <Route path="coupons" element={<FeatureGate feature="coupons.active.max" featureName={t('Coupons')}><Coupons /></FeatureGate>} />
                  <Route path="advertisements" element={<FeatureGate feature="advertisements" featureName={t('Advertisements')}><Advertisements /></FeatureGate>} />
                  <Route path="inventory" element={<FeatureGate feature="inventory" featureName={t('Inventory')}><Inventory /></FeatureGate>} />
                  <Route path="users" element={<AllUsers />} />
                  <Route path="client-orders" element={<FeatureGate feature="orders.monthly" featureName={t('Client Orders')}><AdminOrders /></FeatureGate>} />
                  <Route path="invoices" element={<FeatureGate feature="invoices" featureName={t('Invoices')}><Invoices /></FeatureGate>} />
                  <Route path="reporting" element={<FeatureGate feature="reporting" featureName={t('Reports')}><Reporting /></FeatureGate>} />
                  <Route path="my-company" element={<MyCompany />} />
                  <Route path="subscription" element={<Subscription />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="profile" element={<UserProfile />} />
                </Route>
              ) : (
                <Route
                  element={
                    <UserShell
                      toggleTheme={handleThemeToggle}
                      isDarkMode={isDarkMode}
                      workspace={{ company: companyName, companyId: localStorage.getItem('companyId'), user: auth.username, role: 'user' }}
                    />
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<CustomerDashboard />} />
                  <Route path="shop" element={<BrowseProducts />} />
                  <Route path="shop/:productId" element={<ProductDetail />} />
                  <Route path="my-orders" element={<MyOrders />} />
                  <Route path="invoices" element={<MyInvoices />} />
                  <Route path="profile" element={<UserProfile />} />
                  <Route path="subscription" element={<Subscription />} />
                  <Route path="cart" element={<Cart />} />
                </Route>
              )}
            </Route>
          </Routes>
          </EntitlementsProvider>
        </BrowserRouter>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;
